import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { submitPricingRequest } from "@/lib/api";

const INITIAL = { business_name: "", quantity: "", location: "", contact: "" };

export default function PricingModal({ product, open, onOpenChange }) {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setForm(INITIAL);
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitPricingRequest({ product: `${product.name} — ${product.variant}`, ...form });
      toast.success("Pricing request sent. Our sales team will reach out shortly.");
      onOpenChange(false);
    } catch {
      toast.error("Could not send your request. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-navy px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-lime focus:outline-none transition-colors";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-navy-card border-white/10 text-white sm:max-w-md" data-testid="pricing-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl" data-testid="pricing-modal-title">
            Request Pricing
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {product ? `${product.name} — ${product.variant}` : ""}. Tell us about your business and we will share distributor pricing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 mt-2">
          <input data-testid="pricing-form-business" required placeholder="Business name" className={inputCls} value={form.business_name} onChange={set("business_name")} />
          <input data-testid="pricing-form-quantity" required placeholder="Estimated quantity (e.g. 50 cases / month)" className={inputCls} value={form.quantity} onChange={set("quantity")} />
          <input data-testid="pricing-form-location" required placeholder="Delivery location" className={inputCls} value={form.location} onChange={set("location")} />
          <input data-testid="pricing-form-contact" required placeholder="Email or phone" className={inputCls} value={form.contact} onChange={set("contact")} />
          <button
            type="submit"
            data-testid="pricing-modal-submit-btn"
            disabled={loading}
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-lime px-6 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-sun-bright disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? "Sending…" : "Send Pricing Request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
